import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Eye, Pencil, Copy, Archive, RotateCcw, Trash2, ChevronLeft, ChevronRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, formatDate, StatusBadge } from "./api";
import { imgUrl } from "../lib/api";
import { useAdmin } from "./AdminContext";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "available", label: "Available" },
  { value: "under_offer", label: "Under Offer" },
  { value: "sold", label: "Sold" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

export default function AdminListings() {
  const { user } = useAdmin();
  const [data, setData] = useState({ results: [], total: 0, page: 1 });
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/listings", { params: { q: appliedQ || undefined, status: status || undefined, page, per_page: 12 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [appliedQ, status, page]);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (l) => {
    try {
      await adminApi.post(`/listings/${l.id}/duplicate`);
      toast.success(`"${l.title}" duplicated as a draft.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (l) => {
    if (!window.confirm(`Archive "${l.title}"? It will disappear from the website. You can restore it later.`)) return;
    try {
      await adminApi.post(`/listings/${l.id}/archive`);
      toast.success("Listing archived.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const restore = async (l) => {
    try {
      await adminApi.post(`/listings/${l.id}/restore`);
      toast.success("Listing restored as a draft.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Permanently delete "${l.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/listings/${l.id}`);
      toast.success("Listing deleted.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const totalPages = Math.max(Math.ceil(data.total / 12), 1);
  const inputCls = "border border-ivory/20 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-ivory focus:outline-none";

  return (
    <div data-testid="admin-listings">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-ivory sm:text-4xl">Properties</h1>
          <p className="mt-2 text-sm text-ivory/60">{data.total} listing{data.total === 1 ? "" : "s"} {status ? `· ${STATUS_OPTIONS.find((s) => s.value === status)?.label}` : ""}</p>
        </div>
        <div className="flex gap-3">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/listings.csv`} data-testid="export-csv"
            className="flex items-center gap-2 border border-ivory/25 px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/80 hover:bg-white/10">
            <FileDown className="h-4 w-4" /> Export CSV
          </a>
          <Link to="/admin/listings/new" data-testid="add-new-property" className="flex items-center gap-2 bg-brand px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-brand-dark">
            <Plus className="h-4 w-4" /> Add New Property
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3" data-testid="listings-toolbar">
        <div className="flex min-w-64 flex-1 items-center border border-ivory/20 bg-white/5">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); setAppliedQ(q); } }}
            placeholder="Search by title, street, district or reference…" data-testid="listings-search"
            className="h-12 w-full bg-transparent px-4 text-sm text-ivory placeholder:text-ivory/40 focus:outline-none" />
          <button onClick={() => { setPage(1); setAppliedQ(q); }} aria-label="Search" data-testid="listings-search-btn" className="px-4 text-ivory/60 hover:text-ivory">
            <Search className="h-4 w-4" />
          </button>
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="listings-status-filter" className={`${inputCls} h-12`}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-charcoal">{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="h-7 w-7 animate-spin rounded-full border-2 border-ivory/20 border-t-ivory" /></div>
      ) : data.results.length === 0 ? (
        <div className="mt-8 border border-ivory/10 bg-[#242424] p-14 text-center" data-testid="listings-empty">
          <p className="font-serif text-2xl text-ivory">No properties found</p>
          <p className="mt-2 text-sm text-ivory/50">Try a different search, or add a new property.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto border border-ivory/10" data-testid="listings-table">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead>
              <tr className="border-b border-ivory/10 bg-[#1f1f1f] text-[0.65rem] uppercase tracking-[0.18em] text-ivory/50">
                <th className="px-4 py-3.5 font-semibold">Property</th>
                <th className="px-4 py-3.5 font-semibold">Ref</th>
                <th className="px-4 py-3.5 font-semibold">District</th>
                <th className="px-4 py-3.5 font-semibold">Price</th>
                <th className="px-4 py-3.5 font-semibold">Type</th>
                <th className="px-4 py-3.5 font-semibold">Status</th>
                <th className="px-4 py-3.5 font-semibold">Updated</th>
                <th className="px-4 py-3.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ivory/10">
              {data.results.map((l) => (
                <tr key={l.id} className="bg-[#242424] hover:bg-[#2a2a2a]" data-testid={`listing-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {l.images?.length ? (
                        <img src={imgUrl(l.images[l.cover_index] || l.images[0])} alt="" className="h-12 w-16 shrink-0 object-cover" />
                      ) : (
                        <div className="flex h-12 w-16 shrink-0 items-center justify-center bg-white/5 text-[0.55rem] uppercase text-ivory/40">No photo</div>
                      )}
                      <span className="max-w-56 truncate font-medium text-ivory">{l.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ivory/60">{l.ref}</td>
                  <td className="px-4 py-3 text-ivory/60">{l.district}</td>
                  <td className="px-4 py-3 font-medium text-ivory">{l.price_display}</td>
                  <td className="px-4 py-3 text-ivory/60">{l.type}</td>
                  <td className="px-4 py-3"><StatusBadge status={l.workflow_status} /></td>
                  <td className="px-4 py-3 text-ivory/60">{formatDate(l.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {l.workflow_status !== "draft" && l.workflow_status !== "archived" && (
                        <a href={`/properties/${l.id}`} target="_blank" rel="noopener noreferrer" title="View on website" data-testid={`row-view-${l.id}`} className="p-2 text-ivory/50 hover:text-ivory"><Eye className="h-4 w-4" /></a>
                      )}
                      <Link to={`/admin/listings/${l.id}/edit`} title="Edit" data-testid={`row-edit-${l.id}`} className="p-2 text-ivory/50 hover:text-ivory"><Pencil className="h-4 w-4" /></Link>
                      <button onClick={() => duplicate(l)} title="Duplicate" data-testid={`row-duplicate-${l.id}`} className="p-2 text-ivory/50 hover:text-ivory"><Copy className="h-4 w-4" /></button>
                      {l.workflow_status === "archived" ? (
                        <button onClick={() => restore(l)} title="Restore" data-testid={`row-restore-${l.id}`} className="p-2 text-ivory/50 hover:text-ivory"><RotateCcw className="h-4 w-4" /></button>
                      ) : (
                        <button onClick={() => archive(l)} title="Archive" data-testid={`row-archive-${l.id}`} className="p-2 text-ivory/50 hover:text-ivory"><Archive className="h-4 w-4" /></button>
                      )}
                      {user?.role === "owner" && (
                        <button onClick={() => remove(l)} title="Delete permanently" data-testid={`row-delete-${l.id}`} className="p-2 text-ivory/50 hover:text-ivory"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between" data-testid="listings-pagination">
          <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1} data-testid="page-prev"
            className="flex items-center gap-2 border border-ivory/20 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/70 hover:bg-white/10 disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <p className="text-sm text-ivory/60">Page {data.page} of {totalPages}</p>
          <button onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page >= totalPages} data-testid="page-next"
            className="flex items-center gap-2 border border-ivory/20 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/70 hover:bg-white/10 disabled:opacity-40">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
